/** Parsed lumber dimensions from a material code/name or metadata. */
export type LumberSpec = {
  nominal: string;
  width: number;
  depth: number;
  length: number;
};

/** Transform rule from/to token: WxD (cross-section) or WxDxL (section + length). */
export type TransformSizeToken = {
  nominal: string;
  width: number;
  depth: number;
  /** Present when the token includes length (e.g. 2x4x84). */
  length: number | null;
};

/** Transform from/to: size token, or a catalog material code (e.g. LUM-000337). */
export type TransformRef =
  | { kind: "size"; size: TransformSizeToken }
  | { kind: "code"; code: string };

export type MaterialLumberSource = {
  code: string;
  name: string;
  metadata?: Record<string, unknown>;
};

const LUMBER_RE = /(\d+)\s*[x×]\s*(\d+)\s*[x×]\s*(\d+)/i;
const TOKEN_WDL = /^(\d+)\s*[x×]\s*(\d+)\s*[x×]\s*(\d+)$/i;
const TOKEN_WD = /^(\d+)\s*[x×]\s*(\d+)$/i;

function normalizeNominal(width: number, depth: number): string {
  return `${width}x${depth}`;
}

function parseNominalToken(raw: string): string {
  const parsed = parseTransformSizeToken(raw);
  if (parsed) return parsed.nominal;
  return raw.trim().toLowerCase();
}

export function nominalSizesMatch(a: string, b: string): boolean {
  return parseNominalToken(a) === parseNominalToken(b);
}

/** Parse a transform rule size token (`2x4` or `2x4x84`). */
export function parseTransformSizeToken(raw: string): TransformSizeToken | null {
  const s = raw.trim().toLowerCase();
  if (!s) return null;
  const wdl = s.match(TOKEN_WDL);
  if (wdl) {
    const width = Number(wdl[1]);
    const depth = Number(wdl[2]);
    const length = Number(wdl[3]);
    if (![width, depth, length].every((n) => Number.isFinite(n) && n > 0)) return null;
    return { nominal: normalizeNominal(width, depth), width, depth, length };
  }
  const wd = s.match(TOKEN_WD);
  if (!wd) return null;
  const width = Number(wd[1]);
  const depth = Number(wd[2]);
  if (![width, depth].every((n) => Number.isFinite(n) && n > 0)) return null;
  return { nominal: normalizeNominal(width, depth), width, depth, length: null };
}

/** Extract WxD / WxDxL from a pure token or embedded in a longer label. */
export function extractTransformSize(raw: string): TransformSizeToken | null {
  const s = raw.trim();
  if (!s) return null;
  const exact = parseTransformSizeToken(s);
  if (exact) return exact;
  const embedded = s.match(LUMBER_RE);
  if (!embedded) return null;
  return parseTransformSizeToken(`${embedded[1]}x${embedded[2]}x${embedded[3]}`);
}

/**
 * Parse a transform from/to field for enrich / diagnostics.
 * Pure size tokens → size; otherwise identity string (code or name) as `code`.
 * Resolution should use {@link materialLineMatchesTransformFrom} /
 * {@link findMaterialForTransformTo}, which also try exact names and embedded sizes.
 */
export function parseTransformRef(raw: string): TransformRef | null {
  const s = raw.trim();
  if (!s) return null;
  const pure = parseTransformSizeToken(s);
  if (pure) return { kind: "size", size: pure };
  return { kind: "code", code: s.toLowerCase() };
}

/** True when a parent lumber line matches a transform "from" size token. */
export function materialMatchesTransformFrom(
  material: LumberSpec,
  from: TransformSizeToken,
): boolean {
  if (!nominalSizesMatch(material.nominal, from.nominal)) return false;
  if (from.length != null && material.length !== from.length) return false;
  return true;
}

export function materialCodeMatches(lineCode: string, tokenCode: string): boolean {
  return lineCode.trim().toLowerCase() === tokenCode.trim().toLowerCase();
}

export function materialNameMatches(lineName: string, tokenName: string): boolean {
  return lineName.trim().toLowerCase() === tokenName.trim().toLowerCase();
}

/** Parent line matches transform "from" by code, exact name, or lumber size. */
export function materialLineMatchesTransformFrom(
  line: MaterialLumberSource,
  fromRaw: string,
): boolean {
  const needle = fromRaw.trim();
  if (!needle) return false;
  if (materialCodeMatches(line.code, needle)) return true;
  if (materialNameMatches(line.name, needle)) return true;
  const fromSize = extractTransformSize(needle);
  if (!fromSize) return false;
  const spec = parseLumberFromMaterial(line);
  if (!spec) return false;
  return materialMatchesTransformFrom(spec, fromSize);
}

function specFromMetadata(metadata: Record<string, unknown> | undefined): LumberSpec | null {
  if (!metadata) return null;
  const nominalRaw = metadata.nominal_size ?? metadata.nominal;
  const lengthRaw = metadata.length_in ?? metadata.length;
  if (typeof nominalRaw !== "string" || nominalRaw.trim() === "") return null;
  const token = parseTransformSizeToken(nominalRaw);
  if (!token) return null;
  const width = token.width;
  const depth = token.depth;

  let length = 0;
  if (typeof lengthRaw === "number" && Number.isFinite(lengthRaw)) {
    length = lengthRaw;
  } else if (typeof lengthRaw === "string" && lengthRaw.trim() !== "") {
    length = Number(lengthRaw);
  } else if (token.length != null) {
    length = token.length;
  }
  if (!Number.isFinite(length) || length <= 0) return null;

  return { nominal: token.nominal, width, depth, length };
}

function specFromText(text: string): LumberSpec | null {
  const m = text.match(LUMBER_RE);
  if (!m) return null;
  const width = Number(m[1]);
  const depth = Number(m[2]);
  const length = Number(m[3]);
  if (!Number.isFinite(width) || !Number.isFinite(depth) || !Number.isFinite(length)) return null;
  if (width <= 0 || depth <= 0 || length <= 0) return null;
  return { nominal: normalizeNominal(width, depth), width, depth, length };
}

/** Prefer WxDxL in code/name over metadata — metadata often stores feet. */
export function parseLumberFromMaterial(source: MaterialLumberSource): LumberSpec | null {
  const fromText = specFromText(source.code) ?? specFromText(source.name);
  if (fromText) return fromText;
  return specFromMetadata(source.metadata);
}

export type LumberMaterialLookup = {
  id: string;
  code: string;
  name: string;
  unit: string;
  unit_cost: number;
  metadata?: Record<string, unknown>;
};

export function findMaterialByCode(
  materials: readonly LumberMaterialLookup[],
  code: string,
): LumberMaterialLookup | null {
  const needle = code.trim().toLowerCase();
  if (!needle) return null;
  for (const mat of materials) {
    if (mat.code.trim().toLowerCase() === needle) return mat;
  }
  return null;
}

export function findMaterialByName(
  materials: readonly LumberMaterialLookup[],
  name: string,
): LumberMaterialLookup | null {
  const needle = name.trim().toLowerCase();
  if (!needle) return null;
  for (const mat of materials) {
    if (mat.name.trim().toLowerCase() === needle) return mat;
  }
  return null;
}

/** Exact catalog match by material code, then by name. */
export function findMaterialByIdentity(
  materials: readonly LumberMaterialLookup[],
  raw: string,
): LumberMaterialLookup | null {
  return findMaterialByCode(materials, raw) ?? findMaterialByName(materials, raw);
}

/**
 * Resolve a transform "to" token: exact code/name first, then lumber size.
 */
export function findMaterialForTransformTo(
  materials: readonly LumberMaterialLookup[],
  toRaw: string,
  parentLine: MaterialLumberSource,
): LumberMaterialLookup | null {
  const byIdentity = findMaterialByIdentity(materials, toRaw);
  if (byIdentity) return byIdentity;

  const size = extractTransformSize(toRaw);
  if (!size) return null;
  const parentSpec = parseLumberFromMaterial(parentLine);
  const length = size.length ?? parentSpec?.length ?? 0;
  return findMaterialByLumberSpec(materials, size.nominal, length);
}

/** Lengths to try when matching catalog text (inches, and feet when evenly divisible). */
export function lumberLengthSearchCandidates(lengthInches: number): number[] {
  if (!Number.isFinite(lengthInches) || lengthInches <= 0) return [];
  const out = [lengthInches];
  if (lengthInches >= 24 && lengthInches % 12 === 0) {
    out.push(lengthInches / 12);
  }
  return out;
}

/**
 * Find a catalog material matching target nominal at `length` (inches).
 * Also tries feet-in-third-number labels (e.g. 2x4x7 for 84").
 */
export function findMaterialByLumberSpec(
  materials: readonly LumberMaterialLookup[],
  toNominal: string,
  length: number,
): LumberMaterialLookup | null {
  if (!Number.isFinite(length) || length <= 0) return null;
  const size =
    parseTransformSizeToken(toNominal) ?? extractTransformSize(toNominal);
  if (!size) return null;
  const tw = size.width;
  const td = size.depth;

  for (const len of lumberLengthSearchCandidates(length)) {
    const lengthRe = new RegExp(`${tw}\\s*[x×]\\s*${td}\\s*[x×]\\s*${len}\\b`, "i");
    for (const mat of materials) {
      if (!lengthRe.test(mat.code) && !lengthRe.test(mat.name)) continue;
      const spec = parseLumberFromMaterial(mat);
      if (spec && !nominalSizesMatch(spec.nominal, size.nominal)) continue;
      return mat;
    }
  }

  const candidates = new Set(lumberLengthSearchCandidates(length));
  let fallback: LumberMaterialLookup | null = null;
  for (const mat of materials) {
    const spec = parseLumberFromMaterial(mat);
    if (!spec) continue;
    if (!nominalSizesMatch(spec.nominal, size.nominal)) continue;
    if (candidates.has(spec.length) || spec.length === length) {
      if (!fallback) fallback = mat;
    }
  }
  return fallback;
}
